import React from 'react';
import { Users, NotebookPen, ShieldCheck, CircleDollarSign, Music, MessageSquare, ChevronRight, Sparkles, BarChart3 } from 'lucide-react';
import { WHATSAPP_MESSAGE_URL } from '../constants';
import logoBlanco from '../../assets/Pastoreapp_blanco.webp';

export const LandingHero = () => {
  return (
    <section id="hero" className="relative pt-12 pb-20 md:pt-20 md:pb-32 overflow-hidden bg-slate-50 dark:bg-slate-950">
      
      {/* BACKGROUND GLOW DECORATIONS */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-indigo-500/10 dark:bg-indigo-500/15 blur-[140px] rounded-full pointer-events-none" />
      <div className="absolute bottom-10 right-10 w-96 h-96 bg-violet-500/10 dark:bg-violet-500/15 blur-[120px] rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* HERO HEADER & BADGE */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-200 dark:border-indigo-800/80 text-indigo-700 dark:text-indigo-300 text-xs font-black uppercase tracking-widest animate-in fade-in zoom-in-95 duration-500">
            <Sparkles size={14} className="text-amber-500" />
            <span>Gestión Inteligente para Iglesias</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tight leading-[1.1]">
            Organiza tu iglesia.{' '}
            <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-purple-600 dark:from-indigo-400 dark:via-violet-400 dark:to-purple-400 bg-clip-text text-transparent">
              Acompaña a tu comunidad.
            </span>{' '}
            Crece con propósito.
          </h1>

          <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 font-medium leading-relaxed max-w-3xl mx-auto">
            PastoreApp centraliza miembros, grupos, formación, asistencia, liderazgo, finanzas y gestión ministerial en una sola plataforma.
          </p>

          {/* CTAs */}
          <div className="pt-4 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={WHATSAPP_MESSAGE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-3 shadow-xl shadow-emerald-500/25 active:scale-95 transition-all"
            >
              <MessageSquare size={18} />
              <span>Solicitar Demostración</span>
            </a>
            <a
              href="#plataforma"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#plataforma')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="w-full sm:w-auto px-8 py-4 bg-white dark:bg-slate-900 border-2 border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-200 hover:border-indigo-500 dark:hover:border-indigo-500 rounded-2xl font-black text-xs uppercase tracking-widest flex items-center justify-center gap-2 transition-all"
            >
              <ChevronRight size={16} />
              <span>Ver cómo funciona</span>
            </a>
          </div>

          {/* TRUST MICRO-PHRASE */}
          <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">
            Sin procesos complicados. Una plataforma pensada para la realidad de tu iglesia.
          </p>

          {/* KEY AREAS */}
          <div className="pt-4 flex flex-wrap items-center justify-center gap-3 text-slate-500 dark:text-slate-400 text-xs font-bold">
            {[
              { icon: Users, label: 'Membresía' },
              { icon: NotebookPen, label: 'Formación' },
              { icon: ShieldCheck, label: 'Grupos' },
              { icon: BarChart3, label: 'Liderazgo' },
              { icon: CircleDollarSign, label: 'Finanzas' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                <item.icon size={12} className="text-indigo-500" />
                <span>{item.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* HERO MOCKUP VISUAL - DASHBOARD PREVIEW */}
        <div id="plataforma" className="mt-14 lg:mt-20 max-w-6xl mx-auto">
          <div className="relative rounded-[2.5rem] p-3 sm:p-4 bg-gradient-to-b from-indigo-500/20 via-slate-200/50 to-slate-200/20 dark:from-indigo-500/10 dark:via-slate-800/40 dark:to-slate-900/20 border border-slate-200 dark:border-slate-800 shadow-2xl backdrop-blur-sm">
            
            {/* WINDOW TOP BAR */}
            <div className="bg-white dark:bg-slate-900 rounded-t-[2rem] px-6 py-4 border-b border-slate-200/80 dark:border-slate-800/80 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-rose-500" />
                <div className="w-3 h-3 rounded-full bg-amber-500" />
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="ml-4 text-xs font-bold text-slate-400 dark:text-slate-500 tracking-wider">
                  pastoreapp.cloud/dashboard
                </span>
              </div>
              <div className="hidden sm:flex items-center gap-3">
                <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-full text-[10px] font-black uppercase tracking-wider">
                  Datos de Demostración
                </span>
              </div>
            </div>

            {/* DASHBOARD INNER BODY MOCKUP */}
            <div className="bg-slate-50 dark:bg-slate-950 rounded-b-[2rem] p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6">
              
              {/* SIDEBAR + MAIN LAYOUT */}
              <div className="flex gap-4 lg:gap-6">
                
                {/* SIDEBAR MOCKUP - Hidden on mobile */}
                <div className="hidden lg:flex flex-col w-56 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 space-y-3 shrink-0">
                  <div className="flex items-center gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
                    <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
                      <img src={logoBlanco} alt="" className="w-5 h-5 object-contain" />
                    </div>
                    <span className="text-xs font-black text-slate-900 dark:text-white">PastoreApp</span>
                  </div>
                  {[
                    { icon: BarChart3, label: 'Dashboard', active: true },
                    { icon: Users, label: 'Miembros', active: false },
                    { icon: ShieldCheck, label: 'Grupos', active: false },
                    { icon: NotebookPen, label: 'Formación', active: false },
                    { icon: Music, label: 'Alabanza', active: false },
                    { icon: CircleDollarSign, label: 'Finanzas', active: false },
                  ].map((item, idx) => (
                    <div key={idx} className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-bold transition-colors ${
                      item.active 
                        ? 'bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300' 
                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                    }`}>
                      <item.icon size={14} />
                      <span>{item.label}</span>
                    </div>
                  ))}
                </div>

                {/* MAIN CONTENT */}
                <div className="flex-1 space-y-4 sm:space-y-6 min-w-0">
                  
                  {/* TOP BANNER */}
                  <div className="bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 rounded-2xl p-4 sm:p-6 text-white flex flex-col sm:flex-row items-center justify-between gap-4 border border-white/10 shadow-xl">
                    <div className="space-y-1.5 text-center sm:text-left">
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <Sparkles size={14} className="text-amber-400" />
                        <span className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-300">
                          Panel de Control
                        </span>
                      </div>
                      <h3 className="text-lg sm:text-xl font-black tracking-tight">Bienvenido a PastoreApp</h3>
                      <p className="text-slate-300 text-xs font-medium">
                        Vista general de tu congregación en tiempo real.
                      </p>
                    </div>
                    <div className="w-14 h-14 sm:w-16 sm:h-16 bg-white/10 rounded-2xl p-2.5 border border-white/15 flex items-center justify-center shrink-0">
                      <img src={logoBlanco} alt="PastoreApp" className="w-full h-full object-contain" />
                    </div>
                  </div>

                  {/* STATS GRID */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                    {[
                      { icon: Users, label: 'Miembros Activos', value: '248', color: 'from-indigo-600 to-indigo-800', sub: 'Directorio actualizado' },
                      { icon: ShieldCheck, label: 'Grupos y Células', value: '18', color: 'from-amber-500 to-amber-700', sub: 'Reuniones semanales' },
                      { icon: NotebookPen, label: 'Estudiantes', value: '86', color: 'from-purple-600 to-violet-800', sub: 'En proceso formativo' },
                      { icon: CircleDollarSign, label: 'Movimientos', value: '127', color: 'from-emerald-600 to-emerald-800', sub: 'Registro del mes' },
                    ].map((stat, idx) => (
                      <div key={idx} className={`bg-gradient-to-br ${stat.color} text-white rounded-2xl p-4 sm:p-5 space-y-2 shadow-lg`}>
                        <div className="flex items-center justify-between">
                          <p className="text-[9px] sm:text-[10px] font-black uppercase tracking-widest opacity-80">{stat.label}</p>
                          <stat.icon size={16} className="opacity-80" />
                        </div>
                        <h4 className="text-2xl sm:text-3xl font-black tracking-tight">{stat.value}</h4>
                        <p className="text-[9px] sm:text-[10px] opacity-75 font-semibold">{stat.sub}</p>
                      </div>
                    ))}
                  </div>

                  {/* RECENT ACTIVITY PREVIEW */}
                  <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-4 sm:p-5 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-black text-slate-900 dark:text-white uppercase tracking-wider">Actividad Reciente</h4>
                      <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400">Ver todo</span>
                    </div>
                    <div className="space-y-2">
                      {[
                        { text: 'María López registró asistencia en Grupo Jóvenes', time: 'Hace 2h', color: 'bg-emerald-500' },
                        { text: 'Nuevo estudiante inscrito en Fundamentos de la Fe', time: 'Hace 4h', color: 'bg-indigo-500' },
                        { text: 'Ofrenda especial registrada - Fondo de Infraestructura', time: 'Ayer', color: 'bg-amber-500' },
                      ].map((activity, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-100 dark:border-slate-800">
                          <span className={`w-2 h-2 rounded-full ${activity.color} shrink-0`} />
                          <p className="text-[11px] font-medium text-slate-600 dark:text-slate-400 flex-1 min-w-0 truncate">{activity.text}</p>
                          <span className="text-[10px] text-slate-400 dark:text-slate-500 shrink-0">{activity.time}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>

            </div>
          </div>
        </div>

      </div>
    </section>
  );
};
